/**
 * Extract H2/H3 headings from a Markdown body and produce `{ text, id, level }`
 * entries whose `id` matches EXACTLY what `rehype-slug` (github-slugger)
 * assigns to the corresponding heading element in the DOM. This alignment is
 * what makes the JSON-LD `ItemList` ToC deep-links resolve.
 *
 * The extractor is deliberately conservative:
 *   - only ATX headings (`##`, `###`) at line start
 *   - strips inline markdown (`**bold**`, `*em*`, `` `code` ``, `[link](x)`)
 *   - strips residual HTML tags (defence-in-depth against legacy anchors)
 *   - skips code fences (```…``` blocks) to avoid picking up `## ` inside code
 */
import GithubSlugger from "github-slugger";

const CODE_FENCE_RE = /^(```|~~~)/;
const ATX_HEADING_RE = /^(#{2,3})\s+(.+?)\s*#*\s*$/;

/**
 * Strip common inline Markdown/HTML so the heading text used in JSON-LD is
 * clean plain text (Google's rich results validator rejects raw HTML here).
 */
export function cleanHeadingText(raw) {
  if (typeof raw !== "string") return "";
  let text = raw;
  // Images ![alt](url) → alt
  text = text.replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1");
  // Links [text](url) → text
  text = text.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");
  // Inline code `x` → x
  text = text.replace(/`([^`]+)`/g, "$1");
  // Bold / italic markers **x** *x* __x__ _x_
  text = text.replace(/(\*\*|__)(.*?)\1/g, "$2");
  text = text.replace(/(\*|_)(.*?)\1/g, "$2");
  // Strip HTML tags entirely
  text = text.replace(/<[^>]+>/g, "");
  // Collapse whitespace
  return text.replace(/\s+/g, " ").trim();
}

/**
 * Return the ordered list of `{ text, id, level }` entries for every H2/H3
 * heading in the Markdown source. IDs match rehype-slug output byte-for-byte.
 *
 * Duplicate slugs get numeric suffixes exactly like github-slugger does
 * (`chapter`, `chapter-1`, `chapter-2` …).
 */
export function extractHeadings(md) {
  if (typeof md !== "string" || !md) return [];
  const slugger = new GithubSlugger();
  const lines = md.split("\n");
  const out = [];
  let inFence = false;
  for (const line of lines) {
    if (CODE_FENCE_RE.test(line.trim())) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const m = line.match(ATX_HEADING_RE);
    if (!m) continue;
    const level = m[1].length; // 2 or 3
    const text = cleanHeadingText(m[2]);
    if (!text) continue;
    const id = slugger.slug(text);
    out.push({ text, id, level });
  }
  return out;
}
